import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createInvoiceSharedLink } from '../../../../../../services/invoices';
import pdfMake from 'pdfmake/build/pdfmake';
import { getDate } from '../../../../../../utils/dateUtils';
import PageLoader from '../../../../../../component/PageLoader';
import { generatePdfComponent } from '../../../../../components/generatePdfComponent';

const InvoiceSharedLinkPdfView = () => {
	const viewerRef = useRef(null);
	const [isLoading, setLoading] = useState(false);
	const param = useParams();
	const id = param?.id;
	const navigate = useNavigate();

	async function generatePdf(invoice) {
		const {
			customer,
			invoice_number,
			date,
			due_date,
			remarks,
			organisation,
			invoice_products,
			discount,
			adjustment,
			round_off,
			total_amount,
			payment_method,
		} = invoice;

		function getDataTaxCalculation(productTable) {
			const filteredTaxList = productTable.map((data) => ({
				amount: data.amount,
				tax1: data?.product?.tax1,
				tax2: data?.product?.tax2,
				taxAmount1: (data.amount * parseFloat(data?.product?.tax1 || 0)) / 100,
				taxAmount2: (data.amount * parseFloat(data?.product?.tax2 || 0)) / 100,
			}));

			const groupedTransactions = filteredTaxList.reduce((acc, item) => {
				const tax1 = item.tax1;
				const tax2 = item.tax2;
				const taxAmount1 = parseFloat(item.taxAmount1) || 0;
				const taxAmount2 = parseFloat(item.taxAmount2) || 0;
				const key = `${tax1}_${tax2}`;

				if (!acc[key]) {
					acc[key] = {
						tax1: tax1,
						tax2: tax2,
						taxAmount1: 0,
						taxAmount2: 0,
						count: 0,
					};
				}

				acc[key].taxAmount1 += taxAmount1;
				acc[key].taxAmount2 += taxAmount2;
				acc[key].count += 1;

				return acc;
			}, {});

			const resultArray = Object.values(groupedTransactions);
			return resultArray;
		}

		const docDefinitions = [];
		const productsPerPage = 8;
		const productChunks = [];

		for (let i = 0; i < invoice_products.length; i += productsPerPage) {
			productChunks.push(invoice_products.slice(i, i + productsPerPage));
		}

		productChunks.forEach((chunk, chunkIndex) => {
			const isLastChunk = chunkIndex === productChunks.length - 1;
			docDefinitions.push(
				generatePdfComponent({
					invoice_type: 'SALES INVOICE',
					gst_number: `${organisation?.gst || 'N/A'}`,
					dl_number: `${organisation?.drug_license_number || 'N/A'}`,
					payment_method_section: [
						{
							value: payment_method?.payment_method_name,
							bold: false,
							margin: 10,
						},
					],
					user2: {
						name: customer.name,
						address: customer?.street1 || 'N/A',
						city: customer?.city?.name || 'N/A',
						state: customer?.state?.name || 'N/A',
						country: customer?.country?.name || 'N/A',
						zip_code: customer?.zipcode || 'N/A',
						mobile: customer?.mobile || 'N/A',
						phone: customer?.phone || 'N/A',
					},
					user1: {
						name: organisation.organisation_name,
						address: organisation?.address?.street_1 || 'N/A',
						city: organisation?.address?.city?.name || 'N/A',
						state: organisation?.address?.state?.name || 'N/A',
						country: organisation?.address?.country?.name || 'N/A',
						zip_code: organisation?.address?.postal_code || 'N/A',
						email: '',
						phone: '',
					},

					invoice_detail: [
						{ value: `Invoice No : ${invoice_number}`, bold: false },
						{ value: `Invoice Date: : ${getDate(date)}`, bold: false },
					],

					table_columns: [
						{ label: 'S.No.', key: 's_no', type: 'text', tableColWidth: '6%', fontSize: 6 },
						{
							label: 'Item & Description',
							key: 'item_description',
							type: 'text',
							tableColWidth: '22%',
							fontSize: 6,
						},
						{
							label: 'HSN',
							key: 'hsn',
							type: 'text',
							tableColWidth: '8%',
							fontSize: 6,
						},
						{
							label: 'Qty',
							key: 'qty',
							type: 'text',
							tableColWidth: '8%',
							fontSize: 6,
						},
						{
							label: 'Unit',
							key: 'unit',
							type: 'text',
							tableColWidth: '10%',
							fontSize: 6,
						},
						{
							label: 'Batch',
							key: 'batch',
							type: 'text',
							tableColWidth: '12%',
							fontSize: 6,
						},
						{
							label: 'Expiry',
							key: 'expiry_date',
							type: 'text',
							tableColWidth: '8%',
							fontSize: 6,
						},
						{
							label: 'Rate',
							key: 'rate',
							type: 'number',
							tableColWidth: '8%',
							fontSize: 6,
						},
						{
							label: 'Disc',
							key: 'discount',
							type: 'number',
							tableColWidth: '8%',
							fontSize: 6,
						},
						{
							label: 'Total Amt.',
							key: 'total_amount',
							type: 'number',
							tableColWidth: '10%',
							fontSize: 6,
						},
					],

					table_body_data: chunk.map((data, index) => ({
						s_no: index + 1,
						item_description: data.product.product_name,
						hsn: data?.product?.hsn?.hsn_code,
						qty: data.quantity,
						unit: data.product?.unit?.name,
						batch: data.product_batch?.batch_no,
						discount: data.discount || 0,
						expiry_date: `${getDate(data.expiry, false, false, true)}`,
						rate: data.mrp,
						total_amount: data.amount,
					})),
					taxCalculation: isLastChunk ? getDataTaxCalculation(invoice_products) : null,
					footer_table_columns: isLastChunk
						? [
								{ value: 'Sub Total', key: 'sub_total', bold: false },
								{ value: 'Discount', key: 'discount', bold: false },
								{ value: 'Adjustment', key: 'adjustment', bold: false },
								{ value: 'Round Off', key: 'round_off', bold: false },
								{ value: 'Grand Total', key: 'grand_total', bold: true },
							]
						: [],
					footer_table_data: isLastChunk
						? [
								{
									value: Number(
										chunk.reduce((sum, item) => Number(sum) + Number(item.amount), 0),
									).toFixed(2),
									bold: false,
								},
								{
									value: `${Number(discount || 0).toFixed(2)}`,
									bold: false,
								},
								{
									value: `${Number(adjustment || 0).toFixed(2)}`,
									bold: false,
								},
								{
									value: `${Number(round_off || 0).toFixed(2)}`,
									bold: false,
								},
								{ value: Number(total_amount || 0).toFixed(2), bold: true },
							]
						: [],
					notes: isLastChunk ? remarks : '',
					isLastChunk: isLastChunk,
					totalGrand: isLastChunk ? Number(total_amount).toFixed(2) : null,
				}),
			);
		});

		let mergedContent = [];
		docDefinitions.forEach((doc, index) => {
			mergedContent = mergedContent.concat(doc.content);
			if ((index + 1) % 2 === 0) {
				mergedContent.push({ text: '', pageBreak: 'after' });
			}
		});

		const mergedDocDefinition = {
			pageSize: 'A5',
			pageMargins: [5, 5, 5, 5],
			content: mergedContent,
		};

		pdfMake.createPdf(mergedDocDefinition).getBlob((blob) => {
			const url = URL.createObjectURL(blob);
			viewerRef.current.src = url;
		});
	}

	const getInvoiceDetails = async () => {
		try {
			setLoading(true);
			var res = await createInvoiceSharedLink(id);
			if (res.success) {
				generatePdf(res.data.invoice);
			} else {
				toast.error('Your Link is expiry. Page is redirect to login');
				setTimeout(() => {
					navigate('/');
				}, 3000);
			}
		} catch (error) {
			toast.error(error.message ?? 'Something went wrong!');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getInvoiceDetails();
	}, [id]);

	return (
		<>
			{isLoading ? (
				<PageLoader />
			) : (
				<div style={{ width: '100%', height: '100vh' }}>
					<iframe
						ref={viewerRef}
						style={{ width: '100%', height: '100%', border: 'none' }}
						title="Invoice PDF"
					/>
				</div>
			)}
		</>
	);
};
export default InvoiceSharedLinkPdfView;
